/** @typedef {import('@honeybadger-io/core').Types.Notice} Notice */
import {
  ClientExceptionTypeValues,
  IgnoredError,
} from "./src/types/client-exceptions"
import { SimpleHttpError } from "./src/types/http"

/**
 * Modifies the notice object before notifying based on certain conditions.
 *
 * @param {Notice} notice - The notice object to modify.
 * @returns {void | undefined} Returns undefined if no modification is needed.
 */
export function sharedNoticeModifiers(notice) {
  if (notice && "__promise_stack" in notice) {
    // Promise creation evidence stays secondary when the rejection already has
    // a stack that identifies where the failure occurred.
    notice.context ??= {}
    notice.context.promise_stack ??= notice.__promise_stack
    notice.context.original_stack ??= notice.stack

    if (
      typeof notice.__promise_stack === "string" &&
      (typeof notice.stack !== "string" || notice.stack.trim() === "")
    ) {
      notice.stack = notice.__promise_stack
    }
  }

  let simpleHttpError = undefined

  if (notice && notice instanceof SimpleHttpError) {
    simpleHttpError = notice
  } else if (
    notice &&
    notice.cause &&
    notice.cause instanceof SimpleHttpError
  ) {
    simpleHttpError = notice.cause
  } else if (notice && notice.type === "SimpleHttpError") {
    simpleHttpError = notice
  }

  if (simpleHttpError) {
    const simpleHttpErrorContext = {}

    if (
      simpleHttpError.cause &&
      "name" in simpleHttpError.cause &&
      typeof simpleHttpError.cause.name === "string"
    ) {
      simpleHttpErrorContext._originalName = simpleHttpError.cause.name
    } else if (
      simpleHttpError.cause &&
      "message" in simpleHttpError.cause &&
      typeof simpleHttpError.cause.message === "string"
    ) {
      simpleHttpErrorContext._originalName = simpleHttpError.cause.message
    }

    if (simpleHttpError.response) {
      simpleHttpErrorContext.response = simpleHttpError.response
    }

    if (simpleHttpError.requestMethod) {
      simpleHttpErrorContext.requestMethod =
        typeof simpleHttpError.requestMethod === "string"
          ? simpleHttpError.requestMethod.toUpperCase()
          : simpleHttpError.requestMethod
    }

    if (simpleHttpError.requestPath) {
      simpleHttpErrorContext.requestPath = simpleHttpError.requestPath
      simpleHttpErrorContext.groupableUrl = groupableUrl(
        simpleHttpError.requestPath
      )
    }

    // Regex to match the "statusText" field
    if (simpleHttpError.response?.statusText) {
      simpleHttpErrorContext.statusText = simpleHttpError.response?.statusText
    }

    if (simpleHttpError.message) {
      simpleHttpErrorContext.message = simpleHttpError.message
    }

    notice.name = formatSimpleHttpErrorName(simpleHttpErrorContext, notice)
    notice.context ??= {}
    notice.context.simpleHttpError = simpleHttpErrorContext
    return
  }

  // old simple http error, can maybe get rid of this
  if (/"type"\s*:\s*"SimpleHttpError"/i.test(notice.message)) {
    notice.context ??= {}
    notice.context._originalName = notice.name

    let newName = "SimpleHttpError:"

    const requestPathRegex = /"requestPath"\s*:\s*"([^"]+)"/
    const requestPathMatch = notice.message.match(requestPathRegex)

    const simpleHttpErrorContext = {}

    if (requestPathMatch) {
      let requestPath = requestPathMatch[1]
      simpleHttpErrorContext.requestPath = requestPath
      newName += ` requestPath="${groupableUrl(requestPath)}"`
    }

    // Regex to match the "status" field
    const statusRegex = /"status"\s*:\s*(\d+)/
    const statusMatch = notice.message.match(statusRegex)
    if (statusMatch) {
      simpleHttpErrorContext.status = statusMatch[1]
      newName += ` status=${statusMatch[1]}`
    }

    // Regex to match the "statusText" field
    const statusTextRegex = /"statusText"\s*:\s*"([^"]+)"/
    const statusTextMatch = notice.message.match(statusTextRegex)
    if (statusTextMatch) {
      simpleHttpErrorContext.statusText = statusTextMatch[1]
      newName += ` statusText="${statusTextMatch[1]}"`
    }

    const messageRegex = /"message"\s*:\s*"([^"]+)"/
    const messageMatch = notice.message.match(messageRegex)
    if (messageMatch) {
      simpleHttpErrorContext.message = messageMatch[1]
    }

    notice.name = newName
    notice.context ??= {}
    notice.context.simpleHttpError = simpleHttpErrorContext
    return
  }

  const includeMessageWithName =
    /^error$/i.test(notice.name) || /^typeerror$/i.test(notice.name)

  if (includeMessageWithName && notice.message) {
    notice.context ??= {}
    notice.context._originalName = notice.name

    if (!new RegExp(notice.name, "i").test(notice.message)) {
      notice.name = notice.name + " " + notice.message.slice(0, 250)
    } else {
      notice.name = notice.message.slice(0, 250)
    }

    return
  }

  const useMessageAsName =
    /^window.onunhandledrejection$/i.test(notice.name) ||
    /^window.onerror$/i.test(notice.name)

  if (useMessageAsName && notice.message) {
    notice.context ??= {}
    notice.context._originalName = notice.name

    notice.name = notice.message.slice(0, 250)
    return
  }

  return undefined
}

/**
 * Modifies the notice object before notifying based on certain conditions for server side errors.
 *
 * @param {Notice} notice - The notice object to modify.
 * @returns {void | undefined} Returns undefined if no modification is needed.
 */
export function serverNoticeModifiers(notice) {
  // server side specific modifications

  return sharedNoticeModifiers(notice)
}

/**
 * Shared before notify filters for all environments.
 *
 * @param {Notice} notice - The notice object to modify.
 * @returns {boolean} Returns true if the notice should be ignored.
 */
export function sharedShouldFilterNotice(notice) {
  if (notice.message && /^filter_me_test$/i.test(notice.message)) {
    return true
  }

  if (
    notice instanceof IgnoredError ||
    (notice &&
      typeof notice === "object" &&
      "cause" in notice &&
      notice.cause instanceof IgnoredError) ||
    (notice &&
      typeof notice === "object" &&
      "name" in notice &&
      notice.name === "IgnoredError")
  ) {
    return true
  }

  // The ASCII array for "Unhandled exception. System.". For CSRF errors we return this ambigous message to hide that
  // we are actually checking for a csrf token. I don't want the above searchable in our client js so i'm using the char
  // codes here.
  const arr = [
    85, 110, 104, 97, 110, 100, 108, 101, 100, 32, 101, 120, 99, 101, 112, 116,
    105, 111, 110, 46, 32, 83, 121, 115, 116, 101, 109, 46,
  ]
  const value = arr.reduce((acc, val) => acc + String.fromCharCode(val), "")
  // go ahead and filter out the CSRF error
  if (
    notice.message &&
    typeof notice.message === "string" &&
    notice.message.includes(value)
  ) {
    return true
  }

  // Intermittent browser/client DOM mutation issue from react-snap-carousel; not actionable.
  if (
    notice.message &&
    /\[react-snap-carousel\]: Expected HTMLElement/i.test(notice.message)
  ) {
    return true
  }

  return false
}

/**
 * Shared before notify filters for the server side environments.
 *
 * @param {Notice} notice - The notice object to modify.
 * @returns {boolean} Returns true if the notice should be ignored.
 */
export function serverShouldFilterNotice(notice) {
  // server side specific filters

  return sharedShouldFilterNotice(notice)
}

// Replace the below patterns so they get grouped together in Honeybadger
export function groupableUrl(inUrl) {
  let outputUrl = inUrl.split("?")[0]
  outputUrl = outputUrl.replace(/\b[a-z]{2}-[A-Za-z0-9]{8}\b/g, "{entity_ref}")
  outputUrl = outputUrl.replace(/\/\d+/g, "/{numeric}")

  try {
    // strip out hostname
    outputUrl = new URL(outputUrl).pathname
  } catch (e) {
    // ignore
  }

  return outputUrl
}

function formatSimpleHttpErrorName(simpleHttpErrorContext, notice) {
  if (notice.name == "AuthenticationException") {
    return notice.name
  }

  let message = `[SimpleHttpError]`

  if (ClientExceptionTypeValues.includes(notice.name ?? "")) {
    message = `${notice.name} -`
  }

  if (simpleHttpErrorContext.status) {
    message += ` ${simpleHttpErrorContext.status}`
  } else if (simpleHttpErrorContext.response?.status) {
    message += ` ${simpleHttpErrorContext.response.status}`
  }

  if (simpleHttpErrorContext.requestMethod) {
    message += ` ${simpleHttpErrorContext.requestMethod}`
  }

  if (simpleHttpErrorContext.groupableUrl) {
    message += ` - ${simpleHttpErrorContext.groupableUrl}`
  }

  return message
}
